'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

export type ThemeName = 'midnight' | 'ocean' | 'slate' | 'violet' | 'ember' | 'carbon' | 'aurora';

export interface Theme {
  name: ThemeName;
  label: string;
  description: string;
  preview: string[];
  vars: Record<string, string>;
}

export const THEMES: Theme[] = [
  {
    name: 'midnight',
    label: 'Midnight',
    description: 'Deep navy sidebar with crisp white content',
    preview: ['#0f172a', '#1e293b', '#3b82f6', '#f8fafc'],
    vars: {
      '--sidebar-bg': '#0f172a',
      '--sidebar-border': '#1e293b',
      '--sidebar-text': '#94a3b8',
      '--sidebar-text-active': '#f8fafc',
      '--sidebar-item-active-bg': 'rgba(59,130,246,0.15)',
      '--sidebar-item-active-border': 'rgba(59,130,246,0.4)',
      '--sidebar-item-hover-bg': 'rgba(255,255,255,0.05)',
      '--sidebar-section-label': '#475569',
      '--sidebar-logo-text': '#f8fafc',
      '--topbar-bg': '#ffffff',
      '--topbar-border': '#e2e8f0',
      '--content-bg': '#f8fafc',
      '--card-bg': '#ffffff',
      '--card-border': '#e2e8f0',
      '--primary': '221 83% 53%',
      '--accent': '199 89% 48%',
      '--primary-hex': '#3b82f6',
    },
  },
  {
    name: 'ocean',
    label: 'Ocean',
    description: 'Teal-accented dark sidebar with cool tones',
    preview: ['#0c1a2e', '#0f3460', '#0ea5e9', '#f0f9ff'],
    vars: {
      '--sidebar-bg': '#0c1a2e',
      '--sidebar-border': '#0f3460',
      '--sidebar-text': '#7dd3fc',
      '--sidebar-text-active': '#e0f2fe',
      '--sidebar-item-active-bg': 'rgba(14,165,233,0.15)',
      '--sidebar-item-active-border': 'rgba(14,165,233,0.4)',
      '--sidebar-item-hover-bg': 'rgba(125,211,252,0.07)',
      '--sidebar-section-label': '#1e4976',
      '--sidebar-logo-text': '#e0f2fe',
      '--topbar-bg': '#ffffff',
      '--topbar-border': '#e0f2fe',
      '--content-bg': '#f0f9ff',
      '--card-bg': '#ffffff',
      '--card-border': '#bae6fd',
      '--primary': '199 89% 48%',
      '--accent': '187 96% 43%',
      '--primary-hex': '#0ea5e9',
    },
  },
  {
    name: 'slate',
    label: 'Slate',
    description: 'Clean light sidebar, professional and minimal',
    preview: ['#ffffff', '#f1f5f9', '#3b82f6', '#0f172a'],
    vars: {
      '--sidebar-bg': '#ffffff',
      '--sidebar-border': '#e2e8f0',
      '--sidebar-text': '#64748b',
      '--sidebar-text-active': '#1e40af',
      '--sidebar-item-active-bg': '#eff6ff',
      '--sidebar-item-active-border': '#bfdbfe',
      '--sidebar-item-hover-bg': '#f8fafc',
      '--sidebar-section-label': '#94a3b8',
      '--sidebar-logo-text': '#0f172a',
      '--topbar-bg': '#f8fafc',
      '--topbar-border': '#e2e8f0',
      '--content-bg': '#f1f5f9',
      '--card-bg': '#ffffff',
      '--card-border': '#e2e8f0',
      '--primary': '221 83% 53%',
      '--accent': '199 89% 48%',
      '--primary-hex': '#3b82f6',
    },
  },
  {
    name: 'violet',
    label: 'Violet',
    description: 'Rich purple sidebar with vibrant accents',
    preview: ['#1e1b4b', '#312e81', '#8b5cf6', '#faf5ff'],
    vars: {
      '--sidebar-bg': '#1e1b4b',
      '--sidebar-border': '#312e81',
      '--sidebar-text': '#a5b4fc',
      '--sidebar-text-active': '#ede9fe',
      '--sidebar-item-active-bg': 'rgba(139,92,246,0.2)',
      '--sidebar-item-active-border': 'rgba(139,92,246,0.5)',
      '--sidebar-item-hover-bg': 'rgba(165,180,252,0.07)',
      '--sidebar-section-label': '#4338ca',
      '--sidebar-logo-text': '#ede9fe',
      '--topbar-bg': '#ffffff',
      '--topbar-border': '#ede9fe',
      '--content-bg': '#faf5ff',
      '--card-bg': '#ffffff',
      '--card-border': '#ddd6fe',
      '--primary': '263 70% 50%',
      '--accent': '271 91% 65%',
      '--primary-hex': '#8b5cf6',
    },
  },
  {
    name: 'ember',
    label: 'Ember',
    description: 'Warm charcoal sidebar with amber highlights',
    preview: ['#1c1917', '#292524', '#f59e0b', '#fffbeb'],
    vars: {
      '--sidebar-bg': '#1c1917',
      '--sidebar-border': '#292524',
      '--sidebar-text': '#a8a29e',
      '--sidebar-text-active': '#fef3c7',
      '--sidebar-item-active-bg': 'rgba(245,158,11,0.15)',
      '--sidebar-item-active-border': 'rgba(245,158,11,0.4)',
      '--sidebar-item-hover-bg': 'rgba(255,255,255,0.04)',
      '--sidebar-section-label': '#57534e',
      '--sidebar-logo-text': '#fef3c7',
      '--topbar-bg': '#ffffff',
      '--topbar-border': '#fef3c7',
      '--content-bg': '#fffbeb',
      '--card-bg': '#ffffff',
      '--card-border': '#fde68a',
      '--primary': '38 92% 50%',
      '--accent': '32 95% 44%',
      '--primary-hex': '#f59e0b',
    },
  },
  {
    name: 'carbon',
    label: 'Carbon',
    description: 'Full dark mode with subtle graphite tones',
    preview: ['#111111', '#1a1a1a', '#22d3ee', '#0a0a0a'],
    vars: {
      '--sidebar-bg': '#111111',
      '--sidebar-border': '#222222',
      '--sidebar-text': '#71717a',
      '--sidebar-text-active': '#f4f4f5',
      '--sidebar-item-active-bg': 'rgba(34,211,238,0.1)',
      '--sidebar-item-active-border': 'rgba(34,211,238,0.3)',
      '--sidebar-item-hover-bg': 'rgba(255,255,255,0.04)',
      '--sidebar-section-label': '#3f3f46',
      '--sidebar-logo-text': '#f4f4f5',
      '--topbar-bg': '#0a0a0a',
      '--topbar-border': '#1f1f1f',
      '--content-bg': '#0d0d0d',
      '--card-bg': '#161616',
      '--card-border': '#262626',
      '--primary': '186 100% 42%',
      '--accent': '186 100% 52%',
      '--primary-hex': '#06b6d4',
    },
  },
  {
    name: 'aurora',
    label: 'Aurora',
    description: 'Gradient-rich dark theme with emerald glow',
    preview: ['#0a1628', '#0d2137', '#10b981', '#ecfdf5'],
    vars: {
      '--sidebar-bg': '#0a1628',
      '--sidebar-border': '#0d2137',
      '--sidebar-text': '#6ee7b7',
      '--sidebar-text-active': '#d1fae5',
      '--sidebar-item-active-bg': 'rgba(16,185,129,0.15)',
      '--sidebar-item-active-border': 'rgba(16,185,129,0.4)',
      '--sidebar-item-hover-bg': 'rgba(110,231,183,0.06)',
      '--sidebar-section-label': '#134e4a',
      '--sidebar-logo-text': '#d1fae5',
      '--topbar-bg': '#ffffff',
      '--topbar-border': '#d1fae5',
      '--content-bg': '#f0fdf4',
      '--card-bg': '#ffffff',
      '--card-border': '#bbf7d0',
      '--primary': '160 84% 39%',
      '--accent': '158 64% 52%',
      '--primary-hex': '#10b981',
    },
  },
];

export type DensityMode = 'compact' | 'default' | 'comfortable';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (name: ThemeName) => void;
  themes: Theme[];
  density: DensityMode;
  setDensity: (d: DensityMode) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: THEMES[0],
  setTheme: () => {},
  themes: THEMES,
  density: 'default',
  setDensity: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeName, setThemeName] = useState<ThemeName>('midnight');
  const [density, setDensityState] = useState<DensityMode>('default');

  useEffect(() => {
    const saved = localStorage.getItem('apistrator-theme') as ThemeName | null;
    if (saved && THEMES.find(t => t.name === saved)) setThemeName(saved);
    const savedDensity = localStorage.getItem('apistrator-density') as DensityMode | null;
    if (savedDensity) setDensityState(savedDensity);
  }, []);

  const theme = THEMES.find(t => t.name === themeName) || THEMES[0];

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme.vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    // Apply dark class for carbon/aurora/midnight/ocean/violet/ember dark themes
    const darkThemes: ThemeName[] = ['midnight', 'ocean', 'violet', 'ember', 'carbon', 'aurora'];
    if (darkThemes.includes(theme.name)) {
      root.setAttribute('data-theme-mode', 'dark');
    } else {
      root.setAttribute('data-theme-mode', 'light');
    }
  }, [theme]);

  const setTheme = (name: ThemeName) => {
    setThemeName(name);
    localStorage.setItem('apistrator-theme', name);
  };

  const setDensity = (d: DensityMode) => {
    setDensityState(d);
    localStorage.setItem('apistrator-density', d);
    document.documentElement.setAttribute('data-density', d);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES, density, setDensity }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
